import { get } from 'lodash';
import generateUuid from 'uuid/v4';

// constants
import { applicationTypes } from 'constants/scripts';
import { activityTypes } from 'constants/activities';
import { inputStates } from 'constants/values';
import { resourceType } from 'constants/loading';

// selectors
import { getBagSizeForCrop, isOlEnabled } from 'user/selectors';
import { getCropById, getCropNominalWeights, getNominalWeightByCropId } from 'crops/selectors';
import { getActiveOperationUuid, getActiveUserId } from 'current/selectors';
import { getFieldIdByUuid } from 'fields/selectors';
import {
  isResourcePending,
  createResourceNotPendingCheck
} from 'loading/selectors';
import { getProductById } from 'products/selectors';
import { getProductYearFromSeasonCode } from 'scripts/selectors';
import { hasFatalError } from 'messages/selectors';

// util
import { formatDateToISO8601 } from 'util/formatters/dateFormatter';
import { neverResolve } from 'util/promise';

import {
  fetchAdvancedScriptAvailability,
  fetchHarvestAdjustedYields,
  fetchScriptRecommendations
} from './loaders';
import {
  parseAdvancedScriptAvailability,
  parseAdvancedScriptLayerResults,
  parseAdvancedScriptRecommendationsToTemplateParams,
  parseHankRecommendations
} from './hankParser';
import {
  processAdvancedScriptAvailability,
  processAdvancedScriptTemplate
} from './processors';
import {
  setResourceLoaded, setResourcePending
} from 'loading';
import { cacheHankResponse } from 'scripts';
import { inferUnit } from 'util/unitConversion';

export const findAdvancedScriptAvailability = ({ dispatch, getState, fieldUuid }) => {
  return Promise.resolve() // force a thenable wrapper
  .then( () => {
    const state = getState();
    const userId = getActiveUserId(state);
    const operationUuid = getActiveOperationUuid(state);
    const cropNominalWeights = getCropNominalWeights(state);
    const type = resourceType.ADVANCED_SCRIPT_AVAILABILITY;

    const requestId = generateUuid();
    const pendingPromise = Promise.all([
      fetchAdvancedScriptAvailability({
        fieldUuid,
        cropNominalWeights,
        abortIf: createResourceNotPendingCheck({ getState, type, id: requestId }),
        appIsDead: () => hasFatalError(getState())
      }),
      // TODO - see related todo comment under fetchHarvestAdjustedYield(), and for returning this to
      // simpler code, have a look at the version of this file before 5/4/18.
      fetchHarvestAdjustedYields({userId, operationUuid, fieldUuid, isOlEnabled: isOlEnabled(state)}) 
    ]);

    // We take priority over other calls of this type and signal them to stop
    dispatch(setResourcePending({ type, id: requestId, promise: pendingPromise, exclusive: true }));

    return pendingPromise
    .then((rawResponses) => {
      const availability = parseAdvancedScriptAvailability({
        availability:rawResponses[0],
        adjustedYields:rawResponses[1],
        cropNominalWeights});
      return processAdvancedScriptAvailability({ dispatch, availability });
    })
    .then( processPromise => {
      dispatch(setResourceLoaded({ type, id: requestId }));
      return processPromise;
    });
    // Don't need to hand abort status, because if we aborted the status has been handled
  });
};

export const _buildAdvancedScriptPlantingTemplate = ({ activityUuid, cropId, cropName, croppingYear,
  fieldId, fieldUuid, product, defaultScriptName, seasonCode, zones, zonesSummary, densityCurveField,
  highestEconomicField
}) => ({
  ['activity-type']: activityTypes.PLANTING,
  ['activity-uuid']: activityUuid,
  attributes: {
    crop:cropName,
    id:cropId
  },
  bases: [{ name: 'auto' }],
  ['cropping-year']: croppingYear,
  ['field-id']:fieldId,
  ['field-uuid']:fieldUuid,
  ['fmz-id']:generateUuid(),
  ['fmz-name']:'generated-fmz',
  ['fmz-version']:0,
  id: generateUuid(),
  name: defaultScriptName,
  primary:false, // There is separate logic to set this to primary after storing it.
  season:seasonCode,
  summary:zonesSummary,
  tag: 'auto',
  ['update-time']: formatDateToISO8601(new Date()),
  meta: {
    fieldId,
    name: defaultScriptName,
    products:[product],
    scriptState: inputStates.INSYNC, // Script is always "in sync" with user input right after its generated.
    crop: cropName,
    cropId
  },
  densityCurveField,
  highestEconomicField,
  zones
});

export const findAdvancedScriptTemplate = ({
  dispatch, getState, activityUuid, cropId, defaultScriptName, fieldUuid, goalRatio,
  grainPrice, harvestHistory, numZones, productId, seasonCode, seedCost, targetYield, include,
  countryCode, agronomicSeason, currentAveragePopulation, newCurrentAveragePopulation
}) => {
  return Promise.resolve() // force a thenable wrapper
  .then( () => {
    const state = getState();
    const product = getProductById({state, id:productId, applicationType:applicationTypes.SEED});
    const fieldId = getFieldIdByUuid(state, fieldUuid);
    const cropName = getCropById(state, cropId);
    const croppingYear = getProductYearFromSeasonCode(seasonCode);
    const cropNominalWeight = getNominalWeightByCropId(state, cropId);
    const bagSize = getBagSizeForCrop(state, cropName);

    const type = resourceType.ADVANCED_SCRIPT_RECOMMENDATIONS;
    const requestId = generateUuid();
    let pendingPromise;
    
    const cachedResponse = state.scripts.cachedHankResponse;
    // If the user has moved the slider do not fetch again from Hank, used the catched
    // response instead.
    if (newCurrentAveragePopulation && cachedResponse) {
      pendingPromise = new Promise(resolve => {
        resolve(cachedResponse);
      });
    }
    else {
      pendingPromise = fetchScriptRecommendations({
        fieldUuid, grainPrice, numZones, productId, include, sproutId: product.uuid,
        seedCost, targetYield, cropId, cropNominalWeight, bagSize, harvestHistory, countryCode, agronomicSeason,
        abortIf: createResourceNotPendingCheck({ getState, type, id: requestId }),
        appIsDead: () => hasFatalError(getState())
      });
    }
    // Signal any other pending requests of this type to crawl off and die
    dispatch(setResourcePending({ type, id: requestId, promise: pendingPromise, exclusive: true }));

    return pendingPromise
    .then( recommendations => {
      if(!newCurrentAveragePopulation) {
        dispatch(cacheHankResponse(recommendations));
      }

      if(!isResourcePending({ state: getState(), type, id: requestId })) {
        // should have been pending, crawling off to limbo
        return neverResolve();
      }
      dispatch(setResourceLoaded({ type, id: requestId }));

      const parsedHank = parseHankRecommendations(recommendations);

      // TODO find a new paradigm for this style of kitchen-sink results
      const recommendation = parsedHank.data.asyncRecommendation.recommendation;
      const { densityCurveField, highestEconomicField } = recommendation;
      const hasDeadZone = get(parsedHank, 'data.asyncRecommendation.recommendation.missingDataZoneExists');
      const { zones, zonesSummary } = currentAveragePopulation.q ? parseAdvancedScriptRecommendationsToTemplateParams({
        recommendations: parsedHank, goalRatio, product, cropNominalWeight, grainPrice,
        currentAveragePopulation: recommendation.highestEconomicField.rate
      })
        :  parseAdvancedScriptRecommendationsToTemplateParams({
          recommendations: parsedHank, goalRatio, product, cropNominalWeight, grainPrice});
      const { zones: sliderZones, zonesSummary: sliderZonesSummary }
      = parseAdvancedScriptRecommendationsToTemplateParams({
        recommendations: parsedHank, goalRatio, product, cropNominalWeight, grainPrice,
        currentAveragePopulation: newCurrentAveragePopulation
          ? { q: newCurrentAveragePopulation, u: inferUnit('seed-density').unitKey }
          : currentAveragePopulation
      });
      const { badCciLayers, badYieldLayers } = parseAdvancedScriptLayerResults(parsedHank);
      const template = _buildAdvancedScriptPlantingTemplate({
        activityUuid, cropId, cropName, croppingYear, fieldId,
        fieldUuid, product, defaultScriptName, seasonCode, zones, zonesSummary, densityCurveField,
        highestEconomicField
      });
      const sliderTemplate = currentAveragePopulation.q && _buildAdvancedScriptPlantingTemplate({
        activityUuid, cropId, cropName, croppingYear, fieldId,
        fieldUuid, product, defaultScriptName, seasonCode, zones: sliderZones, zonesSummary: sliderZonesSummary,
        densityCurveField, highestEconomicField
      });
      return processAdvancedScriptTemplate({
        dispatch, getState, template, sliderTemplate, badCciLayers, badYieldLayers, hasDeadZone,
        newCurrentAveragePopulation});
    });
  });
};
